import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /*
   * TEM AUD-1017 · C03b (17-Oct-2026) — "Pagar" con una cantidad escrita y aún sin confirmar.
   * El botón Pagar envía cart-form (updates[]) y el navegador iba al checkout con la cantidad
   * escrita aunque la pantalla mostrara otro total, o con una cantidad que Shopify después
   * ajusta por stock sin que el cliente lo vea. Ahora, si hay algo pendiente, el envío se
   * detiene, se confirman las cantidades contra el carrito, se espera la respuesta y la
   * pantalla se actualiza; solo se continúa al checkout si el carrito quedó exactamente
   * como se ve. Si hubo error, ajuste por stock o un valor inválido, se muestra en la línea
   * y no se continúa. Las actualizaciones se encolan para que no se crucen respuestas.
   */
  /** @type {Set<Promise<any>>} */
  #inflight = new Set();
  /** @type {Promise<any>} */
  #queue = Promise.resolve();
  /** @type {Promise<any> | null} */
  #lastMorph = null;
  #checkoutGuardActive = false;
  /**
   * C03c: el carrito ajustó o rechazó una cantidad (stock) y el cliente todavía no tuvo
   * oportunidad de verlo antes de Pagar. El primer Pagar se detiene y el aviso queda visible.
   */
  #cartNotice = false;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    this.addEventListener('submit', this.#onCheckoutSubmit, true);
    this.addEventListener('tem:quantity-invalid', this.#onQuantityInvalid);
    this.addEventListener('input', this.#onQuantityTyping);
  }

  /** C03b: el selector rechazó un valor vacío, 0 o no entero; mostrarlo en la línea. */
  #onQuantityInvalid = (/** @type {Event} */ event) => {
    const detail = /** @type {CustomEvent} */ (event).detail || {};
    if (detail.line) this.#showLineMessage(detail.line, detail.message);
  };

  /** C03b: al volver a escribir en una casilla, el aviso anterior de esa línea deja de aplicar. */
  #onQuantityTyping = (/** @type {Event} */ event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'number') return;
    const line = Number(input.dataset.cartLine);
    const container = line ? this.refs[`cartItemErrorContainer-${line}`] : null;
    if (container instanceof HTMLElement) container.classList.add('hidden');
    this.#cartNotice = false; // El cliente ya está corrigiendo la cantidad.
  };

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onCheckoutSubmit, true);
    this.removeEventListener('tem:quantity-invalid', this.#onQuantityInvalid);
    this.removeEventListener('input', this.#onQuantityTyping);
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    // C03b: en cola, para que una respuesta vieja no pise a una más nueva.
    this.#queue = this.#queue
      .then(() => this.updateQuantity({ line, quantity, action: 'change' }))
      .catch(() => {});
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    // C03b: registrar la actualización en curso para que Pagar la espere.
    const tracked = deferredUpdatePromise.promise;
    this.#inflight.add(tracked);
    tracked.catch(() => {}).finally(() => this.#inflight.delete(tracked));
    const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        // TEM AUD-1017 · C02 — Shopify puede aplicar PARCIALMENTE el cambio y responder 422
        // {status, message, description} sin `errors` ni `sections` (p. ej. "Debido a la
        // disponibilidad, solo se añadieron 21 artículos"): el carrito del servidor ya tiene 21
        // unidades, pero Horizon dejaba el input, el total y el contador con el valor anterior
        // (reproducido 17-Oct: cart.js = 21, drawer = 1 / L 350). Se normaliza el mensaje y, tras
        // mostrarlo, se vuelve a pedir la sección al servidor para que la UI refleje el estado real.
        if (!parsedResponseText.errors && !parsedResponseText.sections && parsedResponseText.description) {
          parsedResponseText.errors = parsedResponseText.description;
        }

        if (parsedResponseText.errors) {
          this.#cartNotice = true;
          this.#handleCartError(line, parsedResponseText);
          const errorMessage = parsedResponseText.errors;
          // C03b: el re-render trae la línea con el aviso oculto; se vuelve a mostrar después,
          // así el ajuste por stock queda a la vista (antes desaparecía al instante).
          this.#lastMorph = Promise.resolve(
            sectionRenderer.renderSection(this.sectionId, {
              cache: false,
              mode: this.isDrawer ? 'hydration' : 'full',
            })
          )
            .catch(() => {})
            .then(() => this.#showLineMessage(line, `${errorMessage} Revisá el total antes de pagar.`, false));
          // Se resuelve con el carrito real (didError: true) en vez de rechazar, para que el
          // contador del encabezado y los demás oyentes del evento estándar se sincronicen.
          this.fetchCartData()
            .then((cart) => {
              deferredUpdatePromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
                detail: {
                  items: cart.items,
                  itemCount: cart.item_count,
                  source: 'cart-items-component',
                  didError: true,
                  error: errorMessage,
                },
              });
            })
            .catch(() => deferredUpdatePromise.reject(new Error(errorMessage)));
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        // C03c — Shopify también puede responder 200 SIN error y dejar menos unidades de las
        // pedidas (reproducido 18-Oct: se pidió 4 de la tinta 544 negro, existencia 3, el carrito
        // quedó en 3 sin aviso y Pagar siguió al checkout). Se detecta comparando lo pedido con
        // lo que devolvió el carrito y se avisa en la línea.
        const resultItem = Array.isArray(parsedResponseText.items)
          ? parsedResponseText.items.find((/** @type {any} */ it) => it.key === lineId)
          : undefined;
        const adjustedTo =
          config.action === 'change' && quantity > 0 && lineId && (!resultItem || resultItem.quantity < quantity)
            ? resultItem?.quantity ?? 0
            : null;
        if (adjustedTo !== null) this.#cartNotice = true;

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
            adjustedTo,
          },
        });

        this.#lastMorph = Promise.resolve(
          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
            mode: this.isDrawer ? 'hydration' : 'full',
          })
        )
          .catch(() => {})
          .then(() => {
            if (adjustedTo === null) return;
            const message =
              adjustedTo > 0
                ? `Solo pudimos dejar ${adjustedTo} en tu carrito por disponibilidad. Revisá el total antes de pagar.`
                : 'Este producto ya no tiene existencia y se quitó del carrito. Revisá el total antes de pagar.';
            this.#showLineMessage(line, message);
          });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        // C03b: sin respuesta, el total no puede quedar gris (shimmer) como si siguiera cargando.
        resetShimmer(this);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });

    return deferredUpdatePromise.promise;
  }

  /**
   * C03b — Antes de ir al checkout: si hay una cantidad escrita sin confirmar o una
   * actualización en curso, detener el envío, confirmar y continuar solo si quedó bien.
   * @param {SubmitEvent} event
   */
  #onCheckoutSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;

    // Ya hay una verificación en marcha: ignorar el segundo envío (doble clic / doble Enter).
    if (this.#checkoutGuardActive) {
      event.preventDefault();
      return;
    }

    const selectors = /** @type {any[]} */ ([...this.querySelectorAll('cart-quantity-selector-component')]);
    const dirty = this.#cartNotice || this.#inflight.size > 0 || selectors.some((s) => s.hasPendingQuantity?.());
    if (!dirty) return; // Nada pendiente: checkout normal de Shopify.

    event.preventDefault();
    const submitter = /** @type {HTMLButtonElement | null} */ (event.submitter);
    this.#checkoutGuardActive = true;
    this.#setCheckoutBusy(submitter, true);

    let ok = false;
    try {
      ok = await this.#settleQuantitiesBeforeCheckout(selectors);
    } catch (error) {
      console.warn('[cart-items] Pagar: no se pudo confirmar el carrito', error);
      this.#showLineMessage(1, 'No pudimos actualizar tu carrito. Revisá tu conexión e intentá de nuevo.');
      ok = false;
    }
    // Lo que la verificación encontró ya quedó a la vista: el próximo Pagar no se vuelve a detener por eso.
    this.#cartNotice = false;

    if (ok) {
      // form.submit() no vuelve a disparar 'submit'; se agrega el campo que indica checkout.
      if (!form.querySelector('input[type="hidden"][name="checkout"]')) {
        const field = document.createElement('input');
        field.type = 'hidden';
        field.name = 'checkout';
        field.value = '';
        form.append(field);
      }
      form.submit();
      return; // Se navega; se deja el botón ocupado.
    }

    this.#checkoutGuardActive = false;
    this.#setCheckoutBusy(submitter, false);
  };

  /**
   * @param {any[]} selectors
   * @returns {Promise<boolean>} true si el carrito quedó igual a lo que muestra la pantalla.
   */
  async #settleQuantitiesBeforeCheckout(selectors) {
    // 1. Cancelar confirmaciones pendientes (escritura y debounce): las hacemos acá, una sola vez.
    this.#debouncedOnChange.cancel();
    for (const selector of selectors) selector.cancelPendingQuantity?.();

    // 1b. Si se acaba de rechazar un valor inválido, dejar que el cliente vea el aviso.
    const rejected = selectors.map((s) => s.consumeInvalidNotice?.()).some(Boolean);
    if (rejected) return false;

    // 2. Esperar lo que ya esté en vuelo.
    await this.#queue.catch(() => {});
    await Promise.allSettled([...this.#inflight]);

    // 2b. C03c: si el carrito ajustó o rechazó una cantidad (por ejemplo, la que se envió al
    // salir de la casilla justo al tocar Pagar), detenerse: el aviso queda en la línea y el
    // siguiente Pagar ya continúa con lo que el cliente vio.
    if (this.#cartNotice) {
      this.#cartNotice = false;
      if (this.#lastMorph) await this.#lastMorph;
      return false;
    }

    // 3. Leer lo que el cliente ve en cada casilla y validarlo.
    /** @type {{line: number, key: string, quantity: number}[]} */
    const wanted = [];
    const rows = this.refs.cartItemRows ?? [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const input = /** @type {HTMLInputElement | null} */ (row?.querySelector('input[type="number"]'));
      if (!row || !input || input.disabled) continue;
      const line = i + 1;
      const raw = input.value.trim();
      const min = parseInt(input.min) || 1;
      if (!/^\d+$/.test(raw)) {
        input.value = input.getAttribute('value') ?? input.defaultValue;
        this.#showLineMessage(line, 'Ingresá una cantidad válida (un número entero).');
        input.focus();
        return false;
      }
      const quantity = parseInt(raw, 10);
      if (quantity < min) {
        input.value = input.getAttribute('value') ?? input.defaultValue;
        this.#showLineMessage(line, `La cantidad mínima es ${min}. Para quitar el producto usá Eliminar.`);
        input.focus();
        return false;
      }
      wanted.push({ line, key: row.dataset.key ?? '', quantity });
    }

    // 4. Comparar con el carrito real y enviar solo lo que difiere, de a una línea.
    let cart = await this.fetchCartData();
    const byKey = (/** @type {any} */ c, /** @type {string} */ key) => c.items.find((/** @type {any} */ it) => it.key === key);
    for (const w of wanted) {
      const item = byKey(cart, w.key);
      if (item && item.quantity === w.quantity) continue;
      let result;
      try {
        result = await this.updateQuantity({ line: w.line, quantity: w.quantity, action: 'change' });
      } catch (error) {
        // Sin respuesta del carrito: la casilla vuelve a lo que el carrito realmente tiene.
        const input = /** @type {HTMLInputElement | null} */ (rows[w.line - 1]?.querySelector('input[type="number"]'));
        if (input) input.value = input.getAttribute('value') ?? input.defaultValue;
        const actual = item ? ` Tu carrito sigue con ${item.quantity}.` : '';
        this.#showLineMessage(
          w.line,
          `No pudimos actualizar la cantidad.${actual} Revisá tu conexión e intentá de nuevo.`
        );
        return false;
      }
      if (result?.detail?.didError) {
        // Esperar el re-render: el aviso de Shopify queda visible en la línea y no se avanza.
        if (this.#lastMorph) await this.#lastMorph;
        return false;
      }
    }

    // 5. Verificar el resultado y esperar a que la pantalla se actualice.
    await Promise.allSettled([...this.#inflight]);
    if (this.#lastMorph) await this.#lastMorph;
    cart = await this.fetchCartData();
    for (const w of wanted) {
      const item = byKey(cart, w.key);
      if (!item || item.quantity !== w.quantity) {
        const real = item ? item.quantity : 0;
        this.#showLineMessage(
          w.line,
          `Solo pudimos dejar ${real} en tu carrito por disponibilidad. Revisá el total antes de pagar.`
        );
        return false;
      }
    }
    return true;
  }

  /**
   * @param {number} line
   * @param {string} message
   * @param {boolean} [notify] - emitir CartErrorEvent (false si ya se emitió para este error)
   */
  #showLineMessage(line, message, notify = true) {
    const container = this.refs[`cartItemErrorContainer-${line}`];
    const text = this.refs[`cartItemError-${line}`];
    if (container instanceof HTMLElement && text instanceof HTMLElement) {
      text.textContent = message;
      container.classList.remove('hidden');
    }
    if (notify) this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
  }

  /**
   * @param {HTMLButtonElement | null} submitter
   * @param {boolean} busy
   */
  #setCheckoutBusy(submitter, busy) {
    const buttons = submitter
      ? [submitter]
      : /** @type {HTMLButtonElement[]} */ ([...document.querySelectorAll('button[name="checkout"][form="cart-form"]')]);
    for (const button of buttons) {
      button.disabled = busy;
      button.setAttribute('aria-busy', String(busy));
    }
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
